import { useEffect, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { CheckCircle2, Loader2, XCircle } from "lucide-react";
import { errorMessage, post } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { homeFor } from "@/components/RequireRole";
import { usePageTitle } from "@/components/States";
import { Brand } from "@/components/layout/SidebarShell";
import { Button } from "@/components/ui/button";

export default function VerifyEmail() {
  const [params] = useSearchParams();
  const { profile, loading, refreshProfile } = useAuth();
  const [state, setState] = useState({ status: "loading" });
  const started = useRef(false);
  usePageTitle("Confirm your email");

  useEffect(() => {
    if (started.current) return;
    started.current = true;
    const token = params.get("token");
    if (!token) { setState({ status: "error", message: "This confirmation link is incomplete." }); return; }
    post("/auth/verify-email", { token })
      .then(async () => { await refreshProfile(); setState({ status: "ok" }); })
      .catch((e) => setState({ status: "error", message: errorMessage(e) }));
  }, [params, refreshProfile]);

  const next = profile ? homeFor(profile) : "/login";
  return (
    <div className="flex min-h-screen items-center justify-center bg-slate-900 px-6" data-testid="verify-email-page">
      <div className="w-full max-w-md rounded-xl border border-slate-200 bg-white p-8 text-center shadow-xl">
        <div className="mb-5 inline-block rounded-lg bg-slate-900 p-3"><Brand roleLabel="For warrant teams" /></div>
        {(state.status === "loading" || loading) && <Loader2 className="mx-auto h-8 w-8 animate-spin text-blue-700" data-testid="verify-email-loading" />}
        {state.status === "ok" && (
          <div data-testid="verify-email-success">
            <CheckCircle2 className="mx-auto h-10 w-10 text-emerald-600" />
            <h1 className="font-display mt-4 text-2xl font-extrabold text-slate-900">Email confirmed</h1>
            <p className="mt-2 text-sm text-slate-600">Your account is ready to use.</p>
          </div>
        )}
        {state.status === "error" && (
          <div data-testid="verify-email-error">
            <XCircle className="mx-auto h-10 w-10 text-rose-600" />
            <h1 className="font-display mt-4 text-2xl font-extrabold text-slate-900">Link not valid</h1>
            <p className="mt-2 text-sm text-slate-600">{state.message}</p>
          </div>
        )}
        {state.status !== "loading" && !loading && (
          <Button asChild className="mt-6 bg-blue-700 hover:bg-blue-800" data-testid="verify-email-continue">
            <Link to={next}>{profile ? "Continue to Warrant Whizz" : "Sign in"}</Link>
          </Button>
        )}
      </div>
    </div>
  );
}
